import {
  CashSession,
  CashTransaction,
  ManualCashTransactionType,
} from '@/@types/cash-management';
import { PrintLayout, PrintWidth } from '@/@types/print';
import {
  AutoTooltip,
  ConfirmPrintModal,
  CustomMenu,
  EmptyData,
  Modal,
} from '@/components/common';
import { usePrintControls } from '@/modules/core/hooks';
import { Close, Delete, Print } from '@mui/icons-material';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import {
  CashSessionSummaryPrintMethod,
  CashSessionSummaryPrintMode,
} from './useCashSessionSummaryPrint';
import { useCashSessionManualMovementPrint } from './useCashSessionManualMovementPrint';
import { CashSessionStatusLabel } from './CashSessionStatusLabel';
import {
  formatCurrency,
  manualCashTransactionTypes,
  transactionTypeLabel,
} from './cash-management.utils';

type CashSessionDetailModalProps = {
  isOpen: boolean;
  isLoading: boolean;
  cashSession?: CashSession;
  canCreateManualMovement: boolean;
  canCancelManualMovement: boolean;
  canPrintSummary?: boolean;
  isPrintingSummary?: boolean;
  onClose: () => void;
  onOpenManualMovement: () => void;
  // eslint-disable-next-line no-unused-vars
  onCancelManualMovement: (id: string) => void;
  // eslint-disable-next-line no-unused-vars
  onPrintSummary?: (
    // eslint-disable-next-line no-unused-vars
    id: string,
    // eslint-disable-next-line no-unused-vars
    mode: CashSessionSummaryPrintMode,
    // eslint-disable-next-line no-unused-vars
    copies?: number,
    // eslint-disable-next-line no-unused-vars
    selectedLayout?: PrintLayout,
    // eslint-disable-next-line no-unused-vars
    printWidth?: PrintWidth,
    // eslint-disable-next-line no-unused-vars
    method?: CashSessionSummaryPrintMethod
  ) => void;
};

type PrintConfirmAction = 'manual-movement' | 'summary-pos';

const transactionsTableMaxHeightBase = 380;
const transactionsTableMaxHeightXs = 216;

function InfoItem({
  label,
  value,
}: {
  label: string;
  value: string | number | null;
}) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {value || '-'}
      </Typography>
    </Box>
  );
}

export function CashSessionDetailModal({
  isOpen,
  isLoading,
  cashSession,
  canCreateManualMovement,
  canCancelManualMovement,
  canPrintSummary = false,
  isPrintingSummary = false,
  onClose,
  onOpenManualMovement,
  onCancelManualMovement,
  onPrintSummary,
}: CashSessionDetailModalProps) {
  const [printMenuElement, setPrintMenuElement] = useState<null | HTMLElement>(
    null
  );
  const [printConfirmAction, setPrintConfirmAction] =
    useState<PrintConfirmAction>();
  const [selectedManualMovement, setSelectedManualMovement] =
    useState<CashTransaction>();
  const {
    copies,
    handleCopiesChange,
    handleLayoutChange,
    handlePrintWidthChange,
    handlePrintingChange,
    isPrinting,
    selectedLayout,
    selectedPrintWidth,
  } = usePrintControls();
  const {
    isPrintingManualMovement,
    printManualMovement,
    printManualMovementNode,
  } = useCashSessionManualMovementPrint();
  const transactions = cashSession?.transactions || [];
  const declarations = cashSession?.declarations || [];
  const canCancelTransaction = (transaction: CashTransaction) =>
    cashSession?.status === 'OPEN' &&
    canCancelManualMovement &&
    manualCashTransactionTypes.includes(
      transaction.type as ManualCashTransactionType
    );
  const handlePrintSummary = (
    mode: CashSessionSummaryPrintMode,
    printCopies?: number,
    printLayout?: PrintLayout,
    printWidth?: PrintWidth,
    method: CashSessionSummaryPrintMethod = 'browser'
  ) => {
    if (!cashSession || isPrintingSummary) return;

    onPrintSummary?.(
      cashSession.id,
      mode,
      printCopies ?? 1,
      printLayout,
      printWidth,
      method
    );
  };
  const canPrintManualMovement = (transaction: CashTransaction) =>
    canPrintSummary &&
    manualCashTransactionTypes.includes(
      transaction.type as ManualCashTransactionType
    );
  const handleOpenManualMovementPrint = (transaction: CashTransaction) => {
    setSelectedManualMovement(transaction);
    setPrintConfirmAction('manual-movement');
  };
  const handleClosePrintConfirm = () => {
    setPrintConfirmAction(undefined);
    setSelectedManualMovement(undefined);
    handlePrintingChange(false);
    handleCopiesChange(1);
  };
  const handleConfirmPrint = () => {
    if (!cashSession) return;

    if (printConfirmAction === 'summary-pos') {
      handlePrintSummary('pos', copies, selectedLayout, selectedPrintWidth);
      handleClosePrintConfirm();
      return;
    }

    if (printConfirmAction === 'manual-movement' && selectedManualMovement) {
      handlePrintingChange(true);
      printManualMovement(
        {
          ...selectedManualMovement,
          cashSession: selectedManualMovement.cashSession || {
            id: cashSession.id,
            number: cashSession.number,
          },
        },
        copies,
        selectedLayout
      );
      handleClosePrintConfirm();
    }
  };
  const isPrintConfirmLoading =
    isPrinting || isPrintingSummary || isPrintingManualMovement;

  return (
    <Modal open={isOpen} onClose={onClose} fullWidth maxWidth="lg">
      {isLoading || isPrintingSummary ? <LinearProgress /> : null}
      <DialogTitle
        sx={{ p: 2, alignItems: 'center' }}
        display="flex"
        justifyContent="space-between"
      >
        Detalhes do Caixa
        <Stack direction="row" spacing={1}>
          {canPrintSummary ? (
            <IconButton
              aria-label="print"
              disabled={!cashSession || isPrintingSummary}
              onClick={(event) => setPrintMenuElement(event.currentTarget)}
            >
              <Print />
            </IconButton>
          ) : null}
          <IconButton aria-label="close" onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {!cashSession ? (
          <Box py={8}>
            <EmptyData />
          </Box>
        ) : (
          <Stack spacing={3}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4} md={2}>
                  <InfoItem label="Número" value={cashSession.number} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <InfoItem
                    label="Usuário"
                    value={cashSession.user?.name || '-'}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box mt={0.5}>
                    <CashSessionStatusLabel status={cashSession.status} />
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <InfoItem label="Aberto em" value={cashSession.openedAt} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <InfoItem
                    label="Conferência em"
                    value={cashSession.reconciliationStartedAt}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <InfoItem label="Fechado em" value={cashSession.closedAt} />
                </Grid>
                <Grid item xs={6} sm={3} md={3}>
                  <InfoItem
                    label="Saldo Inicial"
                    value={formatCurrency(cashSession.startingCash)}
                  />
                </Grid>
                <Grid item xs={6} sm={3} md={3}>
                  <InfoItem
                    label="Saldo Previsto"
                    value={formatCurrency(cashSession.expectedCash)}
                  />
                </Grid>
                <Grid item xs={6} sm={3} md={3}>
                  <InfoItem
                    label="Declarado"
                    value={formatCurrency(cashSession.declaredCash)}
                  />
                </Grid>
                <Grid item xs={6} sm={3} md={3}>
                  <InfoItem
                    label="Diferença"
                    value={formatCurrency(cashSession.discrepancy)}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Stack spacing={1}>
              <Typography variant="h6" fontWeight="bold">
                Movimentações
              </Typography>
              <Divider />
              {transactions.length ? (
                <TableContainer
                  sx={{
                    maxHeight: {
                      xs: transactionsTableMaxHeightXs,
                      sm: transactionsTableMaxHeightBase,
                    },
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Documento</TableCell>
                        <TableCell align="right">Valor</TableCell>
                        <TableCell>Descrição</TableCell>
                        <TableCell>Criado em</TableCell>
                        <TableCell align="center">Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions.map((transaction) => (
                        <TableRow
                          key={transaction.id}
                          sx={{
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <TableCell
                            sx={{
                              width: '10%',
                            }}
                          >
                            {transactionTypeLabel[transaction.type]}
                          </TableCell>
                          <TableCell
                            sx={{
                              width: '10%',
                            }}
                          >
                            {transaction.documentType?.name || '-'}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              width: '15%',
                            }}
                          >
                            {formatCurrency(transaction.amount)}
                          </TableCell>
                          <TableCell
                            sx={{
                              minWidth: { xs: '120px', sm: '200px' },
                              maxWidth: { xs: '250px', sm: 0 },
                              width: { xs: '70%', md: '40%' },
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <AutoTooltip
                              title={transaction.description || '-'}
                              disableInteractive
                            >
                              <span>{transaction.description || '-'}</span>
                            </AutoTooltip>
                          </TableCell>
                          <TableCell
                            sx={{
                              width: '15%',
                            }}
                          >
                            {transaction.createdAt}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              width: '10%',
                            }}
                          >
                            {canPrintManualMovement(transaction) ? (
                              <Tooltip
                                title="Imprimir (Bobina)"
                                disableInteractive
                              >
                                <IconButton
                                  size="small"
                                  disabled={isPrintConfirmLoading}
                                  onClick={() =>
                                    handleOpenManualMovementPrint(transaction)
                                  }
                                >
                                  <Print fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                            {canCancelTransaction(transaction) ? (
                              <>
                                <Tooltip title="Excluir" disableInteractive>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() =>
                                      onCancelManualMovement(transaction.id)
                                    }
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <EmptyData sx={{ height: '8rem' }} />
              )}
            </Stack>

            <Stack spacing={1}>
              <Typography variant="h6" fontWeight="bold">
                Conferência
              </Typography>
              <Divider />
              {declarations.length ? (
                <TableContainer>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Documento</TableCell>
                        <TableCell align="right">Previsto</TableCell>
                        <TableCell align="right">Declarado</TableCell>
                        <TableCell align="right">Diferença</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {declarations.map((declaration) => (
                        <TableRow
                          key={declaration.documentTypeId}
                          sx={{
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <TableCell>
                            {declaration.documentType?.name || '-'}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(declaration.expectedAmount)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(declaration.declaredAmount)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(declaration.discrepancy)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <EmptyData sx={{ height: '8rem' }} />
              )}
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3 }}>
        {cashSession?.status === 'OPEN' && canCreateManualMovement ? (
          <Button variant="contained" onClick={onOpenManualMovement}>
            Movimentar
          </Button>
        ) : null}
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
      <CustomMenu
        anchorEl={printMenuElement}
        onClose={() => setPrintMenuElement(null)}
        menuItems={[
          { title: 'Imprimir', onClick: () => handlePrintSummary('pdf') },
          {
            title: 'Imprimir (Bobina)',
            onClick: () => {
              setPrintMenuElement(null);
              setPrintConfirmAction('summary-pos');
            },
          },
        ]}
      />
      {printManualMovementNode}
      <ConfirmPrintModal
        isOpen={!!printConfirmAction}
        isLoading={isPrintConfirmLoading}
        title="Imprimir?"
        message={
          printConfirmAction === 'manual-movement'
            ? 'Deseja imprimir esta movimentação manual?'
            : 'Deseja imprimir o resumo do caixa em bobina?'
        }
        onClose={handleClosePrintConfirm}
        onConfirm={handleConfirmPrint}
        confirmButtonText={
          printConfirmAction === 'summary-pos' ? 'Imprimir' : undefined
        }
        onIntentConfirm={
          printConfirmAction === 'summary-pos'
            ? () => {
                handlePrintSummary(
                  'pos',
                  copies,
                  selectedLayout,
                  selectedPrintWidth,
                  'intent'
                );
                handleClosePrintConfirm();
              }
            : undefined
        }
        onLayoutChange={handleLayoutChange}
        onCopiesChange={handleCopiesChange}
        copies={copies}
        selectedLayout={selectedLayout}
        showLayoutSelector={printConfirmAction !== 'summary-pos'}
        showPrintWidthSelector={printConfirmAction === 'summary-pos'}
        selectedPrintWidth={selectedPrintWidth}
        onPrintWidthChange={handlePrintWidthChange}
      />
    </Modal>
  );
}
